import classNames from "classnames"
import React, { useCallback, useState } from "react"

import { Analytics } from "@bounce/analytics"
import { Button, Title3 } from "@bounce/design-system"
import type { GeocodingResult } from "@bounce/google-place"
import { useTranslation } from "@bounce/i18n"

import { HIGH_RATING, STORE_TYPES } from "./constants"
import { StoreList } from "./StoreList"
import { StoreListSectionLoading } from "./StoreListSectionLoading"
import { StoreListSkeleton } from "./StoreListSkeleton"
import type { PoiData } from "../../../libs/bounce/pois"
import { useInterpolations } from "../../../screens/LuggageStorage/CityScreen/hooks/useInterpolations"
import { StoreCard } from "@/components/LuggageStorage/Store/StoreCard/StoreCard"
import { useSectionViewed } from "@/hooks/useSectionViewed"
import { InteractionEvents } from "@/libs/analytics/events"
import type { CityData } from "@/libs/bounce/cities"
import { FilterChips } from "@/screens/LuggageStorage/CityListViewScreen/components/FilterChips/FilterChips"
import type { StoreOrderByTagProps } from "@/screens/LuggageStorage/CityListViewScreen/components/OrderSelector/CityListViewOrderSelector"
import { CityListViewOrderSelector } from "@/screens/LuggageStorage/CityListViewScreen/components/OrderSelector/CityListViewOrderSelector"
import {
  DEFAULT_ORDER_BY_KEY,
  ORDER_BY_MAPPING,
  ORDER_BY_OPTIONS,
} from "@/screens/LuggageStorage/CityListViewScreen/constants"
import { useStoresQuery } from "@/screens/LuggageStorage/CityListViewScreen/hooks/useStoresQuery"
import type { PageType } from "@/types"

type StoreListSectionProps = {
  className?: string
  from: PageType
  path: string
  searchQuery: string
  sectionViewedMeta?: object
  subtitle?: React.ReactNode
  title?: React.ReactNode
  // Core data
  city: CityData
  poi?: PoiData
  // Optional search functionality
  searchTerm?: GeocodingResult | undefined
  // Optional initial filter state
  initialFilters?: {
    open247Filter?: boolean | null
    openNowFilter?: boolean | null
    highRating?: boolean | null
    selectedTypes?: string[]
    selectedValues?: string[]
  }
}

export const StoreListSection = ({
  className,
  from,
  path,
  searchQuery,
  sectionViewedMeta,
  subtitle,
  title,
  city,
  poi,
  searchTerm,
  initialFilters = {},
}: StoreListSectionProps) => {
  const { t } = useTranslation()
  const interpolations = useInterpolations({ city })
  const { sectionRef } = useSectionViewed({ name: "StoreListView", meta: sectionViewedMeta })

  /* Filters state */
  const [open247Filter, setOpen247Filter] = useState<boolean | null>(initialFilters.open247Filter ?? null)
  const [openNowFilter, setOpenNowFilter] = useState<boolean | null>(initialFilters.openNowFilter ?? null)
  const [highRating, setHighRating] = useState<boolean | null>(initialFilters.highRating ?? null)
  const [selectedValues, setSelectedValues] = useState<string[]>(initialFilters.selectedValues ?? [])
  const [order, setOrder] = useState<StoreOrderByTagProps>(DEFAULT_ORDER_BY_KEY)
  const [selectedTypes, setSelectedTypes] = useState<string[]>(initialFilters.selectedTypes ?? [])

  /* Loading state */
  const [isFakeLoading, setIsFakeLoading] = useState(false)

  const { stores, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } = useStoresQuery({
    from,
    citySlug: from === "City" ? city.slug : undefined,
    poiSlug: from === "POI" ? poi?.slug : undefined,
    initialSuggestions: true,
    filters: {
      coordinates: searchTerm?.coordinates,
      isOpen247: open247Filter ? open247Filter : undefined,
      minRating: highRating ? HIGH_RATING : undefined,
      openNow: !!openNowFilter,
      orderBy: ORDER_BY_MAPPING[order],
      types: selectedTypes.length ? selectedTypes : [],
    },
  })

  const isLoading = (isFetching && !isFetchingNextPage) || isFakeLoading

  const trackFilterEvent = (value: string, from: string, subtype?: string) => {
    Analytics.track(InteractionEvents.LuggageStorageFilterClicked, {
      filterType: value,
      from,
      ...(subtype && { subtype }),
    })
  }

  const trackSortEvent = (value: string, from: string, subtype: string) => {
    Analytics.track(InteractionEvents.LuggageStorageSortingApplied, {
      filterType: value,
      from,
      subtype,
    })
  }

  const onSeeMore = () => {
    Analytics.track(InteractionEvents.LuggageStorageSeeMoreClicked, {
      currentStores: stores.length,
      from,
    })

    void fetchNextPage()
  }

  const handleChipClick = useCallback(
    (value: string) => {
      if (STORE_TYPES.includes(value)) {
        setSelectedTypes(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]))
        trackFilterEvent("Type", from, value)
        return
      }

      if (value === "availability") {
        setIsFakeLoading(true)
        setTimeout(() => setIsFakeLoading(false), 1000)
      }

      setSelectedValues(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]))
      trackFilterEvent(value, from)

      if (value === "open247") {
        setOpen247Filter(prev => (prev ? null : true))
        return
      }

      if (value === "highRating") {
        setHighRating(prev => (prev ? null : true))
        return
      }

      if (value === "openNow") {
        setOpenNowFilter(prev => (prev ? null : true))
        return
      }
    },
    [from],
  )

  const clearFilters = useCallback(() => {
    setSelectedValues([])
    setSelectedTypes([])
    setOpen247Filter(null)
    setOpenNowFilter(null)
    setHighRating(null)
  }, [])

  // Generate dynamic titles based on filters
  const cityFilterTitle = t(
    "screen.storeList.storesGrid.titleV3",
    "{count, plural, =1 {Most popular storage location} other {# most popular storage locations}}  out of {NUM_LOCATIONS} in {CITY_NAME}",
    {
      count: stores.length,
      CITY_NAME: interpolations.CITY_NAME,
      NUM_LOCATIONS: interpolations.STORAGE_SPOT_COUNT,
    },
  )

  const addressFilterTitle = t(
    "screen.storeList.storesGrid.addressFilterTitle",
    "Most popular storage locations out of {NUM_LOCATIONS}",
    {
      NUM_LOCATIONS: interpolations.STORAGE_SPOT_COUNT,
    },
  )

  const resultsHeader = title || (searchTerm ? addressFilterTitle : cityFilterTitle)

  return (
    <section ref={sectionRef} className={classNames("mx-auto flex w-full max-w-screen-lg flex-col", className)}>
      <div className="px-6 lg:px-0">
        {!!resultsHeader && (
          <div className="flex flex-row items-center justify-between">
            <h3 className="align-left text-md font-semibold">{resultsHeader}</h3>
            <CityListViewOrderSelector
              onOrderChange={newOrder => {
                setOrder(newOrder)
                trackSortEvent("Order", from, newOrder)
              }}
              currentStoreOrderBy={order}
              storeOrderTags={ORDER_BY_OPTIONS}
            />
          </div>
        )}
        {!!subtitle && <Title3 className="!paragraph1 mt-2 text-neutral-800">{subtitle}</Title3>}
      </div>

      <div className="mx-auto w-full max-w-screen-lg">
        <FilterChips
          className="px-6 pt-4 lg:px-0"
          selectedTypes={selectedTypes}
          selectedValues={selectedValues}
          onChipClick={handleChipClick}
          onDropdownSelect={handleChipClick}
        />
      </div>

      {isLoading && <StoreListSkeleton />}

      {!isLoading && stores.length === 0 && <StoreListSectionLoading onClear={clearFilters} />}

      {!isLoading && stores.length > 0 && (
        <StoreList from={from} path={path} searchQuery={searchQuery} stores={stores}>
          {store => <StoreCard from={from} store={store} />}
        </StoreList>
      )}

      {hasNextPage && stores.length > 0 && (
        <section className="flex w-full max-w-screen-lg justify-center pt-16">
          <Button as="button" variant="secondary" onPress={onSeeMore} disabled={isFetchingNextPage}>
            {isFetchingNextPage
              ? t("screen.storeList.seeMore.loading", "Loading...")
              : t("screen.storeList.seeMore.cta", "See more")}
          </Button>
        </section>
      )}
    </section>
  )
}
